import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { CAMPAIGN_START_VAN_SAVED_LIST_IMPORT } from "src/graphql/campaignContactImport";
import {
  campaignStartVanSavedListImport,
  campaignStartVanSavedListImportVariables
} from "src/graphql/generated/campaignStartVanSavedListImport";
import {
  getCampaign_campaign,
  getCampaign_campaign_contactImportJob
} from "src/graphql/generated/getCampaign";
import { JobStatus, JobType } from "src/graphql/generated/globalTypes";
import useMutation from "src/util/useMutation";

import { useCampaignEditEvents } from "../../CampaignEditorsProvider";
import { ImportStatus } from "../ContactSources";
import JobComplete from "../JobComplete";
import JobRunning from "../JobRunning";
import VanSavedListPicker from "./VanSavedListPicker";

type State =
  | "PICKING_VAN_SAVED_LIST"
  | "IMPORTING_VAN_SAVED_LIST"
  | "VAN_SAVED_LIST_IMPORTED";

const hasVanImport = (campaign: getCampaign_campaign): boolean =>
  campaign.contactImportJob?.type === JobType.CAMPAIGN_VAN_SAVED_LIST_IMPORT;

export const hasValidVanImport = (campaign: getCampaign_campaign): boolean => {
  if (hasVanImport(campaign) && campaign.vanIntegration) {
    const args = JSON.parse(campaign.contactImportJob!.args ?? "{}");
    return (
      args.vanIntegrationId &&
      args.vanIntegrationId === campaign.vanIntegration.vanIntegration.id &&
      args.databaseMode &&
      args.databaseMode === campaign.vanIntegration.databaseMode
    );
  } else {
    return false;
  }
};

const stateFromJob = (job: getCampaign_campaign_contactImportJob) => {
  switch (job.status) {
    case JobStatus.QUEUED:
    case JobStatus.RUNNING:
      return "IMPORTING_VAN_SAVED_LIST";
    default:
      return "VAN_SAVED_LIST_IMPORTED";
  }
};

const VanImport: React.FC<{
  campaign: getCampaign_campaign;
  onChangeImportStatus: (status: ImportStatus) => void;
  onClearContacts: () => void;
  overwriting: boolean;
  refreshCampaign: () => void;
}> = ({
  campaign,
  onChangeImportStatus,
  onClearContacts,
  overwriting,
  refreshCampaign
}) => {
  const [state, setState] = useState<State>(
    hasValidVanImport(campaign) && !overwriting
      ? stateFromJob(campaign.contactImportJob!)
      : "PICKING_VAN_SAVED_LIST"
  );
  useEffect(() => {
    if (hasValidVanImport(campaign)) {
      setState(stateFromJob(campaign.contactImportJob!));
    }
  }, [campaign]);
  useEffect(() => {
    if (overwriting) setState("PICKING_VAN_SAVED_LIST");
  }, [overwriting]);
  useEffect(() => {
    switch (state) {
      case "PICKING_VAN_SAVED_LIST":
        onChangeImportStatus("PREPARING");
        break;
      case "IMPORTING_VAN_SAVED_LIST":
        onChangeImportStatus("IN_PROGRESS");
        break;
      case "VAN_SAVED_LIST_IMPORTED":
        onChangeImportStatus("DONE");
    }
  }, [state, onChangeImportStatus]);

  const sendEditEvent = useCampaignEditEvents();
  const { enqueueSnackbar } = useSnackbar();

  const [startVanSavedListImportJob] = useMutation<
    campaignStartVanSavedListImport,
    campaignStartVanSavedListImportVariables
  >(CAMPAIGN_START_VAN_SAVED_LIST_IMPORT);

  async function startVanSavedListImport(listId: number) {
    const { errors } = await startVanSavedListImportJob({
      variables: {
        campaignId: campaign.id,
        listId
      }
    });
    if (errors) {
      enqueueSnackbar(errors[0]?.message ?? "Failed to start contact import", {
        variant: "error"
      });
      setState("PICKING_VAN_SAVED_LIST");
    } else {
      sendEditEvent();
      // There is a bug in apollo-client where query data doesn't automatically update based on
      // mutation results. We need to explicitly refetch the campaign to make sure that the
      // campaign details state updates with the new job.
      // https://github.com/apollographql/apollo-client/issues/5963
      refreshCampaign();
    }
  }

  switch (state) {
    case "PICKING_VAN_SAVED_LIST":
      return (
        <VanSavedListPicker
          campaign={campaign}
          onSubmit={startVanSavedListImport}
        />
      );
    case "IMPORTING_VAN_SAVED_LIST":
      return <JobRunning job={campaign.contactImportJob!} />;
    case "VAN_SAVED_LIST_IMPORTED":
      return (
        <JobComplete
          onClearFile={() => {
            setState("PICKING_VAN_SAVED_LIST");
            onClearContacts();
          }}
          campaign={campaign}
          dataSourceFieldLabel="VAN field"
        />
      );
  }
};

export default VanImport;
